import { PageHeader } from "@/widgets/page-header"
import { useEffect, useMemo, useRef } from "react"
import { BookFlightFormValues, bookFlightValidationSchema, fetchFlight, selectFlight, selectFlightLoading, selectLoadedFlightId, selectSelectedFareFamilyFlightId, selectSelectedFlight } from "../model"
import { selectAirportsFirstLoad, selectAircraftFirstLoad, selectCountriesFirstLoad, selectAirlinesFirstLoad, fetchAircraft, fetchAirports, fetchAirlines, fetchCountries } from "@/entities/aviabooking-reference"
import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { useParams, useNavigate } from "react-router-dom"
import { FlightDetails } from "./flight-details"
import { FareFamilies } from "./fare-families"
import { Loader } from "@/shared/ui/loader"
import { PassengersInfo } from "./passengers-info"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import notify from "@/shared/lib/notify"
import { getErrorMessage } from "@/shared/consts/error-codes"
import { bookFlight, IBookDataItem, IBookTraveller, DocumentType, IAncillaryService } from "@/shared/api/aviabooking"
import { reset as resetBookings } from "@/entities/booking"
import { ContactInfo } from "./contact-info"
import { ButtonLoader } from "@/shared/ui/button-loader"
import { GenderType } from "@/shared/api/aviabooking"

const initial: BookFlightFormValues = {
    phone: '+7',
    travellers: []
}

export const BookFlightPage = () => {
    const isFirstRender = useRef(true)
    const { id } = useParams()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const flight = useAppSelector(selectFlight)
    const selectedFlight = useAppSelector(selectSelectedFlight)
    const selectedFlightId = useAppSelector(selectSelectedFareFamilyFlightId)
    const airportsFirstLoad = useAppSelector(selectAirportsFirstLoad)
    const aircraftFirstLoad = useAppSelector(selectAircraftFirstLoad)
    const countriesFirstLoad = useAppSelector(selectCountriesFirstLoad)
    const airlinesFirstLoad = useAppSelector(selectAirlinesFirstLoad)
    const flightLoading = useAppSelector(selectFlightLoading)
    const loadedFlightId = useAppSelector(selectLoadedFlightId)
    const isFlightReady = loadedFlightId === id && !flightLoading

    const onSubmit: SubmitHandler<BookFlightFormValues> = async (data) => {
        const travellers: IBookTraveller[] = []
        const ancillaryServices: IAncillaryService[] = []
        const dataItems: IBookDataItem[] = []
        data.travellers.forEach((traveller) => {
            travellers.push({
                id: traveller.id,
                type: traveller.type,
                first_name: traveller.first_name,
                last_name: traveller.last_name,
                middle_name: traveller.middle_name,
                date_of_birth: traveller.date_of_birth,
                nationality: traveller.nationality,
                gender: traveller.gender,
                ...(traveller.save_document ? { save_document: true } : {}),
            })
            dataItems.push({
                traveller_ref: traveller.id,
                id_document: {
                    number: traveller.document_number,
                    type: traveller.document_type,
                    ...(traveller.document_type === DocumentType.Passport
                        ? { issued_at: traveller.expired_at || undefined }
                        : { expired_at: traveller.expired_at || undefined }
                    ),
                    issue_country_code: traveller.issue_country_code,
                },
            })
            Object.entries(traveller.selected_seats).forEach(([, v]) => {
                dataItems.push({
                    traveller_ref: traveller.id,
                    segment_ref: v.segmentRef,
                    booked_seat: {
                        number: `${v.rowNumber}${v.number}`, // row 10, seat A => 10A
                        rfisc: v.rfisc ?? ""
                    }
                })
            })
        })

        try {
            await bookFlight(selectedFlightId, {
                phone: data.phone,
                travellers,
                data_items: dataItems,
                ancillary_services: ancillaryServices
            })
            dispatch(resetBookings())
            notify.success("Билет успешно забронирован")
            navigate('/avia-booking/bookings')
        } catch (e) {
            notify.error(getErrorMessage(e.response?.data?.error_id))
        }
    }

    const updateTravellers = () => {
        if (!flight) return
        const travellers: BookFlightFormValues['travellers'] = []
        let lastTravellerId = 1
        flight.prices[0]?.fares?.forEach((fare) => {
            for (let i = 0; i < fare.quantity; i++) {
                travellers.push({
                    id: lastTravellerId,
                    type: fare.type,
                    first_name: "",
                    last_name: "",
                    date_of_birth: "",
                    nationality: "",
                    gender: GenderType.Male,
                    document_type: DocumentType.Unspecified,
                    issue_country_code: "",
                    document_number: "",
                    expired_at: "",
                    save_document: false,
                    selected_seats: {},
                })
                lastTravellerId += 1
            }
        })
        methods.setValue('travellers', travellers)
    }

    useEffect(() => {
        if (id !== loadedFlightId) {
            dispatch(fetchFlight(id))
                .unwrap()
                .catch(() => {
                    navigate('/404', { replace: true })
                })
        }
    }, [id, loadedFlightId])

    useEffect(() => {
        if (aircraftFirstLoad) {
            dispatch(fetchAircraft())
        }
        if (airportsFirstLoad) {
            dispatch(fetchAirports())
        }
        if (airlinesFirstLoad) {
            dispatch(fetchAirlines())
        }
        if (countriesFirstLoad) {
            dispatch(fetchCountries())
        }
        isFirstRender.current = false
    }, [])


    const methods = useForm({
        defaultValues: initial,
        resolver: yupResolver(bookFlightValidationSchema)
    })

    const travellers = methods.watch('travellers')

    const {
        ticketsTotal,
        taxesTotal,
        seatsTotal,
        totalPrice,
        currency
    } = useMemo(() => {
        const price = selectedFlight?.prices?.[0]

        if (!price) {
            return {
                ticketsTotal: 0,
                taxesTotal: 0,
                seatsTotal: 0,
                totalPrice: 0,
                currency: ''
            }
        }

        const taxesTotal = price.fares.reduce((sum, fare) => sum + fare.quantity * fare.taxes, 0)
        const ticketsTotal = price.fares.reduce((sum, fare) => sum + fare.quantity * (fare.total_fare - fare.taxes), 0)
        const currency = price.fares[0]?.currency ?? ''

        const seatsTotal = travellers.reduce((sum, traveller) => {
            return sum + Object.values(traveller.selected_seats).reduce((s, seat) => s + (seat.price?.amount ?? 0), 0)
        }, 0)

        const totalPrice = parseFloat((ticketsTotal + taxesTotal + seatsTotal).toFixed(2))

        return {
            ticketsTotal,
            taxesTotal,
            seatsTotal,
            totalPrice,
            currency
        }
    }, [selectedFlight, travellers])

    useEffect(() => {
        updateTravellers()
    }, [flight])

    return (
        <div className="_page_container">
            <PageHeader title="Покупка билетов" />
            {isFlightReady &&
                <FormProvider {...methods}>
                    <form onSubmit={methods.handleSubmit(onSubmit)}>
                        <div className="flexbox">
                            <FlightDetails />
                            <FareFamilies />
                            <PassengersInfo />
                            <ContactInfo />
                            {(Boolean(ticketsTotal) || Boolean(taxesTotal)) &&
                                <div className="w-[400px] flex flex-col gap-1">
                                    <div className="flex justify-between">
                                        <div>
                                            Стоимость билетов
                                        </div>
                                        <div>
                                            {ticketsTotal} {currency}
                                        </div>
                                    </div>
                                    <div className="flex justify-between">
                                        <div>
                                            Налоги и сборы
                                        </div>
                                        <div>
                                            {taxesTotal} {currency}
                                        </div>
                                    </div>
                                    {Boolean(seatsTotal) &&
                                        <div className="flex justify-between">
                                            <div>
                                                Места
                                            </div>
                                            <div>
                                                {seatsTotal} {currency}
                                            </div>
                                        </div>
                                    }
                                    {Boolean(totalPrice) &&
                                        <div className="font-bold flex justify-between">
                                            <div>
                                                Итого за билеты
                                            </div>
                                            <div>
                                                {totalPrice} {currency}
                                            </div>
                                        </div>
                                    }
                                </div>
                            }
                            <button
                                disabled={methods.formState.isSubmitting}
                                type="submit"
                                className="w-[400px] inline-flex items-center justify-center gap-3 py-6 text-lg rounded-xl font-bold text-white bg-color-primary hover:bg-color-primary/70 transition"
                            >
                                Продолжить
                                {methods.formState.isSubmitting && <ButtonLoader />}
                            </button>
                        </div>
                    </form>
                </FormProvider>
            }
            {!isFlightReady &&
                <div className="info_panel">
                    <Loader />
                </div>
            }
        </div>
    )
}
